import React, { useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { Web3Context } from '../../context/Web3Context';
import { formatUSDT, formatDate, shortenAddress } from '../../utils/formatters';
import { PACKAGE_NAMES, PACKAGE_PRICES, PACKAGE_COLORS, PACKAGE_STATUS_COLORS, INCOME_COLORS, ACTIVITY_TYPES, CEILING_LIMITS } from '../../utils/constants';
import PieChart from './PieChart';
import './Dashboard.css';

function AnimatedNumber({ value, prefix = '$', duration = 1500 }) {
  const [display, setDisplay] = useState('0.00');
  const animRef = useRef(null);
  useEffect(() => {
    const target = parseFloat(value.replace(/,/g, ''));
    if (isNaN(target) || target === 0) { setDisplay(value); return; }
    const start = performance.now();
    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;
      setDisplay(current.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      if (progress < 1) animRef.current = requestAnimationFrame(tick);
    }
    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [value, duration]);
  return <>{prefix}{display}</>;
}

export default function Dashboard({ onNavigate, notify }) {
  const { userId, readContracts, contracts } = useContext(Web3Context);
  const [claiming, setClaiming] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [ceilingStatus, setCeilingStatus] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !readContracts.views) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Parallel load: dashboard, info, breakdown, activities, royalty claimables, level counts, ceilings (7)
        const ceilingCalls = [];
        for (let i = 0; i < 7; i++) {
          ceilingCalls.push(readContracts.core.checkCeilingAvailable(userId, i, ethers.constants.MaxUint256).catch(() => [false, ethers.BigNumber.from(0), ethers.BigNumber.from(0)]));
        }
        const results = await Promise.all([
          readContracts.views.getUserDashboard(userId),
          readContracts.views.getUserInfo(userId),
          readContracts.views.getUserIncomeBreakdown(userId),
          readContracts.views.getUserActivities(userId, 0, 10),
          readContracts.views.getUserClaimableElite(userId),
          readContracts.views.getUserClaimableGlobal(userId),
          (readContracts.core.getLevelTeamCounts ? readContracts.core.getLevelTeamCounts(userId).catch(() => Array(30).fill(0)) : Promise.resolve(Array(30).fill(0))),
          ...ceilingCalls
        ]);
        if (cancelled) return;

        const [dash, info, breakdown, acts, eliteC, globalC, lvlTeamCounts, ...ceilingResults] = results;

        const cStatuses = ceilingResults.map((res, i) => {
          const active = info.packageActive[i];
          if (!active) {
            const hasHigherActive = info.packageActive.slice(i + 1).some(a => a);
            if (hasHigherActive) return 'ceilingHit';
            return 'inactive';
          }
          const limitWei = ethers.utils.parseUnits(CEILING_LIMITS[i].toString(), 18);
          const remaining = res[1];
          const used = limitWei.sub(remaining.gt(limitWei) ? limitWei : remaining);
          const percent = limitWei.gt(0) ? used.mul(100).div(limitWei).toNumber() : 0;
          if (percent >= 75) return 'nearCeiling';
          return 'active';
        });

        const highestActive = [...info.packageActive].lastIndexOf(true);

        setData({
          userId, referrerId: info.referrerId.toNumber(), wallet: info.wallet,
          highestPackage: highestActive >= 0 ? PACKAGE_NAMES[highestActive] : 'None',
          highestPackageColor: highestActive >= 0 ? PACKAGE_COLORS[highestActive] : 'rgba(255,255,255,0.2)',
          totalIncome: dash.totalIncome,
          claimableLevel: dash.claimableLevel,
          claimableAutoLevel: dash.claimableAutoLevel,
          claimableElite: eliteC,
          claimableGlobal: globalC,
          claimableDividend: dash.claimableDividend,
          eliteEligible: dash.eliteEligible,
          globalEligible: dash.globalEligible,
          totalDirectRefs: info.totalDirectRefs.toNumber(),
          teamTotal: Array.isArray(lvlTeamCounts) ? lvlTeamCounts.reduce((a, c) => a + (c.toNumber ? c.toNumber() : Number(c) || 0), 0) : 0,
          packageActive: info.packageActive,
          boardIncome: breakdown.boardIncome,
          directIncome: breakdown.directIncome,
          levelIncome: breakdown.levelIncome,
          autoLevelIncome: breakdown.autoLevelIncome,
          royaltyIncome: breakdown.royaltyIncome,
          dividendIncome: breakdown.dividendIncome
        });
        setCeilingStatus(cStatuses);

        const actItems = [];
        for (let i = 0; i < acts.types.length; i++) {
          if (acts.types[i] === 0) continue;
          actItems.push({ type: acts.types[i], amount: acts.amounts[i], timestamp: acts.timestamps[i] });
        }
        setActivities(actItems);
      } catch (err) {
        if (!cancelled) notify.error('Failed to load dashboard: ' + (err.reason || err.message));
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId, readContracts.views, refreshKey]);

  function copyReferralLink() {
    const link = window.location.origin + '/?ref=' + userId;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); notify.success('Referral link copied!'); }).catch(() => fallbackCopy(link));
    } else { fallbackCopy(link); }
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
    document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    setCopied(true); setTimeout(() => setCopied(false), 2000); notify.success('Referral link copied!');
  }

  if (loading) return <div className="page-loading"><div className="mini-spinner" /><span>Loading dashboard...</span></div>;
  if (!data) return <div className="page-empty">Unable to load dashboard</div>;

  const totalClaimable = [data.claimableLevel, data.claimableAutoLevel, data.claimableElite, data.claimableGlobal, data.claimableDividend]
    .reduce((sum, v) => { try { return sum + parseFloat(formatUSDT(v).replace(/,/g, '')); } catch { return sum; } }, 0);
  const totalTeam = data.teamTotal || data.totalDirectRefs;
  const pieData = [
    { label: 'Board', value: parseFloat(formatUSDT(data.boardIncome).replace(/,/g, '')) || 0, color: INCOME_COLORS.board },
    { label: 'Direct', value: parseFloat(formatUSDT(data.directIncome).replace(/,/g, '')) || 0, color: INCOME_COLORS.direct },
    { label: 'Level', value: parseFloat(formatUSDT(data.levelIncome).replace(/,/g, '')) || 0, color: INCOME_COLORS.level },
    { label: 'Auto Level', value: parseFloat(formatUSDT(data.autoLevelIncome).replace(/,/g, '')) || 0, color: INCOME_COLORS.autoLevel },
    { label: 'Elite Royalty Pool', value: parseFloat(formatUSDT(data.claimableElite).replace(/,/g, '')) || 0, color: INCOME_COLORS.eliteRoyalty },
    { label: 'Global Royalty Pool', value: parseFloat(formatUSDT(data.claimableGlobal).replace(/,/g, '')) || 0, color: INCOME_COLORS.globalRoyalty },
    { label: 'Dividend', value: parseFloat(formatUSDT(data.dividendIncome).replace(/,/g, '')) || 0, color: INCOME_COLORS.dividend }
  ];
  const displayActivities = showAllActivities ? activities.slice(0, 10) : activities.slice(0, 5);

  return (
    <div className="dash">
      <div className="dash-user-card animate-in" style={{ animationDelay: '0s' }}>
        <div className="dash-user-avatar">
          <svg width="36" height="36" viewBox="0 0 36 36"><circle cx="18" cy="18" r="18" fill="rgba(0,229,255,0.1)"/><circle cx="18" cy="13" r="6" fill="rgba(0,229,255,0.4)"/><path d="M6 30c0-6.627 5.373-12 12-12s12 5.373 12 12" fill="rgba(0,229,255,0.2)"/></svg>
        </div>
        <div className="dash-user-info">
          <div className="dash-user-id">ID: {data.userId}</div>
          <div className="dash-user-meta"><span>Upline: {data.referrerId}</span><span>{shortenAddress(data.wallet)}</span></div>
        </div>
        <div className="dash-user-pkg-badge" style={{ background: data.highestPackageColor + '18', color: data.highestPackageColor, borderColor: data.highestPackageColor + '40' }}>{data.highestPackage}</div>
      </div>

      <div className="dash-earnings-card animate-in" style={{ animationDelay: '0.05s' }}>
        <div className="dash-earnings-bg" />
        <div className="dash-earnings-particles">{Array.from({ length: 8 }).map((_, i) => (<span key={i} className="dash-particle" style={{ left: `${10 + Math.random() * 80}%`, animationDelay: `${Math.random() * 3}s`, animationDuration: `${3 + Math.random() * 2}s` }} />))}</div>
        <span className="dash-earnings-label">Total Earnings</span>
        <div className="dash-earnings-amount"><AnimatedNumber value={formatUSDT(data.totalIncome)} /></div>
        <div className="dash-earnings-badges">
          {data.eliteEligible && <span className="dash-badge-elite">Elite</span>}
          {data.globalEligible && <span className="dash-badge-global">Global</span>}
        </div>
      </div>

      <div className="dash-claim-card animate-in" style={{ animationDelay: '0.1s' }}>
        <div className="dash-claim-glow" />
        <span className="dash-claim-label">Total Claimable</span>
        <span className="dash-claim-amount">${totalClaimable.toFixed(2)}</span>
        <button className="dash-claim-btn" onClick={async () => {
          if (claiming) return;
          setClaiming(true);
          try {
            const loadId = notify.loading('Claiming all income...');
            const tx = await contracts.pools.claimAll();
            await tx.wait();
            notify.removeNotification(loadId);
            notify.success('All income claimed!');
            setRefreshKey(k => k + 1);
          } catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
          setClaiming(false);
        }} disabled={claiming}>
          <span className="dash-claim-btn-pulse" /><span className="dash-claim-btn-text">{claiming ? 'CLAIMING...' : 'CLAIM NOW'}</span>
        </button>
      </div>

      <div className="dash-ref-card animate-in" style={{ animationDelay: '0.12s' }}>
        <span className="dash-ref-label">Your Referral Link</span>
        <div className="dash-ref-row">
          <span className="dash-ref-link">{window.location.origin}/?ref={userId}</span>
          <button className={`dash-ref-copy ${copied ? 'dash-ref-copied' : ''}`} onClick={copyReferralLink}>{copied ? 'Copied!' : 'Copy'}</button>
        </div>
      </div>

      <div className="dash-team-row animate-in" style={{ animationDelay: '0.14s' }}>
        <div className="dash-team-stat">
          <div className="dash-team-icon" style={{ background: 'rgba(0,229,255,0.1)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00e5ff" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          </div>
          <span className="dash-team-value">{data.totalDirectRefs}</span>
          <span className="dash-team-label">Direct Referrals</span>
        </div>
        <div className="dash-team-stat">
          <div className="dash-team-icon" style={{ background: 'rgba(168,85,247,0.1)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M9 7a4 4 0 100-8 4 4 0 000 8zM16 3.13a4 4 0 010 7.75"/></svg>
          </div>
          <span className="dash-team-value">{totalTeam}</span>
          <span className="dash-team-label">Total Team</span>
        </div>
      </div>

      <div className="dash-income-section animate-in" style={{ animationDelay: '0.16s' }}>
        <span className="dash-section-title">Income Distribution</span>
        <div className="dash-income-chart-wrap"><PieChart data={pieData} size={180} /></div>
        <div className="dash-income-legend">
          {pieData.map((item, i) => (
            <div key={i} className="dash-legend-item">
              <span className="dash-legend-dot" style={{ background: item.color }} />
              <span className="dash-legend-label">{item.label}</span>
              <span className="dash-legend-value" style={{ color: item.color }}>${item.value.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dash-pkg-section animate-in" style={{ animationDelay: '0.18s' }}>
        <span className="dash-section-title">Package Status</span>
        <div className="dash-pkg-row">
          {PACKAGE_NAMES.map((name, i) => {
            const status = ceilingStatus[i] || 'inactive';
            const color = PACKAGE_STATUS_COLORS[status];
            return (
              <div key={i} className="dash-pkg-indicator" onClick={() => onNavigate('packages')}>
                <div className="dash-pkg-circle" style={{
                  borderColor: color,
                  background: status !== 'inactive' ? color + '15' : 'rgba(255,255,255,0.03)',
                  boxShadow: status === 'active' ? `0 0 12px ${color}40` : 'none'
                }}>
                  <span style={{ color: status !== 'inactive' ? color : 'rgba(255,255,255,0.2)', fontSize: '10px', fontWeight: 800 }}>
                    ${PACKAGE_PRICES[i]}
                  </span>
                </div>
                <span className="dash-pkg-name" style={{ color: status !== 'inactive' ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)' }}>{name}</span>
              </div>
            );
          })}
        </div>
        <div className="dash-pkg-legend-row">
          <span className="dash-pkg-legend"><span style={{ background: PACKAGE_STATUS_COLORS.active }} className="dash-legend-mini" />Active</span>
          <span className="dash-pkg-legend"><span style={{ background: PACKAGE_STATUS_COLORS.nearCeiling }} className="dash-legend-mini" />Near Ceiling</span>
          <span className="dash-pkg-legend"><span style={{ background: PACKAGE_STATUS_COLORS.ceilingHit }} className="dash-legend-mini" />Ceiling Hit</span>
          <span className="dash-pkg-legend"><span style={{ background: PACKAGE_STATUS_COLORS.inactive }} className="dash-legend-mini" />Inactive</span>
        </div>
      </div>

      <div className="dash-activity-section animate-in" style={{ animationDelay: '0.22s' }}>
        <div className="dash-activity-header">
          <span className="dash-section-title">Recent Activity</span>
          {activities.length > 5 && (
            <button className="dash-activity-toggle" onClick={() => setShowAllActivities(!showAllActivities)}>{showAllActivities ? 'Show Less' : 'View All'}</button>
          )}
        </div>
        {displayActivities.length === 0 ? (
          <div className="page-empty" style={{ minHeight: '80px' }}>No activity yet</div>
        ) : (
          <div className="dash-activity-list">
            {displayActivities.map((act, i) => {
              const info = ACTIVITY_TYPES[act.type] || { label: 'Transaction', icon: '📋' };
              return (
                <div key={i} className="dash-activity-item">
                  <span className="dash-activity-icon">{info.icon}</span>
                  <div className="dash-activity-info">
                    <span className="dash-activity-label">{info.label}</span>
                    <span className="dash-activity-time">{formatDate(act.timestamp)}</span>
                  </div>
                  <span className="dash-activity-amount">${formatUSDT(act.amount)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
